/**
 * Data-source hook for the Employees page (104c).
 *
 * Replaces the old `useTeamSettings` hook, which was misnamed — this page
 * is now the authoritative Employees surface, not the Team/Memberships
 * surface. See 104 § Q7.
 *
 * Mockup-mode branch reads from `src/data/employees.ts` + `businessUnits.ts`
 * and enriches the roster with the admin fields the live API returns
 * (userLogin, collectorId, deactivatedAt, userId, sourceNativeId). Fixtures
 * currently carry a single deactivated row so the Show-deactivated toggle
 * has something to reveal.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';
import { EMPLOYEES, EMPLOYEES_DEACTIVATED_AT } from '@/data/employees';
import { BUSINESS_UNITS, type BusinessUnit } from '@/data/businessUnits';

/**
 * Admin view of an Employee — the superset of fields returned by
 * `GET /api/v1/orgs/:orgId/employees`. Deliberately distinct from
 * `@/types/entities`'s `Employee`, which is the slimmer roster shape used
 * by dashboards / timesheets / rules. Don't broaden that one.
 */
export interface EmployeeAdmin {
  id: string;
  name: string;
  email: string | null;
  userLogin: string | null;
  businessUnitId: string | null;
  collectorId: string | null;
  deactivatedAt: string | null;
  userId: string | null;
  sourceNativeId: string | null;
}

export interface UseEmployeesAdminParams {
  includeDeactivated?: boolean;
  search?: string;
}

export interface UseEmployeesAdminResult {
  employees: readonly EmployeeAdmin[];
  businessUnits: readonly BusinessUnit[];
  isLoading: boolean;
}

function toAdmin(
  row: EmployeeAdmin | (typeof EMPLOYEES)[number],
  deactivatedAt: string | null = null,
): EmployeeAdmin {
  return {
    id: row.id,
    name: row.name,
    email: 'email' in row ? (row.email ?? null) : null,
    userLogin: 'userLogin' in row ? ((row as EmployeeAdmin).userLogin ?? null) : null,
    businessUnitId:
      'businessUnitId' in row
        ? ((row as EmployeeAdmin).businessUnitId ?? row.businessUnitId ?? null)
        : null,
    collectorId: 'collectorId' in row ? ((row as EmployeeAdmin).collectorId ?? null) : null,
    deactivatedAt,
    userId: 'userId' in row ? ((row as EmployeeAdmin).userId ?? null) : null,
    sourceNativeId:
      'sourceNativeId' in row ? ((row as EmployeeAdmin).sourceNativeId ?? null) : null,
  };
}

function mockEmployees(includeDeactivated: boolean, search: string | undefined): EmployeeAdmin[] {
  const needle = (search ?? '').trim().toLowerCase();
  const all = EMPLOYEES.map((e, idx): EmployeeAdmin => {
    const deactivatedAt = EMPLOYEES_DEACTIVATED_AT[e.id] ?? null;
    // Alternate between linked (collector-sourced, linked to user) and
    // reporting-only so the badges are visible. Indexing is a presentational
    // convenience — no product rule behind it.
    const isLinked = idx % 2 === 0;
    const isCollectorSourced = idx % 3 !== 0;
    return {
      id: e.id,
      name: e.name,
      email: e.email,
      userLogin: e.email ? e.email.split('@')[0] : null,
      businessUnitId: e.businessUnitId,
      collectorId: isCollectorSourced ? 'col-insightful-meridian' : null,
      deactivatedAt,
      userId: isLinked ? `usr-${e.id}` : null,
      sourceNativeId: isCollectorSourced ? `native-${e.id}` : null,
    };
  });
  const filtered = includeDeactivated ? all : all.filter((e) => e.deactivatedAt === null);
  if (!needle) return filtered;
  return filtered.filter((e) => {
    const hay = `${e.name} ${e.email ?? ''} ${e.userLogin ?? ''}`.toLowerCase();
    return hay.includes(needle);
  });
}

export function useEmployeesAdmin(params: UseEmployeesAdminParams = {}): UseEmployeesAdminResult {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;
  const includeDeactivated = params.includeDeactivated ?? false;
  const search = params.search;

  const employeesQuery = useQuery({
    queryKey: ['employeesAdmin', orgId, includeDeactivated, search ?? ''],
    queryFn: async () => {
      const token = await getToken();
      const qs = new URLSearchParams();
      if (includeDeactivated) qs.set('includeDeactivated', 'true');
      if (search && search.trim() !== '') qs.set('search', search.trim());
      const qsStr = qs.toString();
      const res = await apiFetch<{ employees: EmployeeAdmin[] }>(
        `/api/v1/orgs/${orgId}/employees${qsStr ? `?${qsStr}` : ''}`,
        token,
      );
      return res.employees.map((e) => toAdmin(e, e.deactivatedAt));
    },
    enabled: !mockupMode && !!orgId,
  });

  const buQuery = useQuery({
    queryKey: ['businessUnits', orgId],
    queryFn: async () => {
      const token = await getToken();
      const res = await apiFetch<{
        businessUnits: Array<{ id: string; name: string }>;
      }>(`/api/v1/orgs/${orgId}/business-units`, token);
      return res.businessUnits;
    },
    enabled: !mockupMode && !!orgId,
  });

  if (mockupMode) {
    return {
      employees: mockEmployees(includeDeactivated, search),
      businessUnits: BUSINESS_UNITS,
      isLoading: false,
    };
  }

  return {
    employees: employeesQuery.data ?? [],
    businessUnits: buQuery.data ?? [],
    isLoading: employeesQuery.isLoading || buQuery.isLoading,
  };
}

// ── Mutation hooks ──────────────────────────────────────────────────────
// All admin mutations invalidate the list so the page refetches without
// a manual refresh. In mockup mode the mutations short-circuit to a
// resolved promise so the UI can be exercised without a backend; the
// fixtures are read-only, so the list does not update — this mirrors the
// credential-rotation modal precedent.

function useInvalidator() {
  const { currentOrg } = useAppContext();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['employeesAdmin', currentOrg.id] });
  };
}

export interface CreateEmployeeBody {
  name: string;
  email?: string;
  userLogin?: string;
  businessUnitId?: string;
}

export function useCreateEmployee() {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const invalidate = useInvalidator();

  return useMutation({
    mutationFn: async (body: CreateEmployeeBody) => {
      if (mockupMode) return { mockup: true as const };
      const token = await getToken();
      return apiFetch<{ employee: EmployeeAdmin }>(
        `/api/v1/orgs/${currentOrg.id}/employees`,
        token,
        { method: 'POST', body: JSON.stringify(body) },
      );
    },
    onSuccess: invalidate,
  });
}

export interface EditEmployeeBody {
  id: string;
  name?: string;
  email?: string | null;
  userLogin?: string | null;
  businessUnitId?: string | null;
}

export function useEditEmployee() {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const invalidate = useInvalidator();

  return useMutation({
    mutationFn: async ({ id, ...body }: EditEmployeeBody) => {
      if (mockupMode) return { mockup: true as const };
      const token = await getToken();
      return apiFetch<{ employee: EmployeeAdmin }>(
        `/api/v1/orgs/${currentOrg.id}/employees/${id}`,
        token,
        { method: 'PATCH', body: JSON.stringify(body) },
      );
    },
    onSuccess: invalidate,
  });
}

export function useDeactivateEmployee() {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const invalidate = useInvalidator();

  return useMutation({
    mutationFn: async (id: string) => {
      if (mockupMode) return { mockup: true as const };
      const token = await getToken();
      return apiFetch<unknown>(`/api/v1/orgs/${currentOrg.id}/employees/${id}/deactivate`, token, {
        method: 'POST',
      });
    },
    onSuccess: invalidate,
  });
}

export function useReactivateEmployee() {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const invalidate = useInvalidator();

  return useMutation({
    mutationFn: async (id: string) => {
      if (mockupMode) return { mockup: true as const };
      const token = await getToken();
      return apiFetch<unknown>(`/api/v1/orgs/${currentOrg.id}/employees/${id}/reactivate`, token, {
        method: 'POST',
      });
    },
    onSuccess: invalidate,
  });
}

export function useLinkUser() {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const invalidate = useInvalidator();

  return useMutation({
    mutationFn: async ({ employeeId, userId }: { employeeId: string; userId: string }) => {
      if (mockupMode) return { mockup: true as const };
      const token = await getToken();
      return apiFetch<unknown>(
        `/api/v1/orgs/${currentOrg.id}/employees/${employeeId}/link-user`,
        token,
        { method: 'POST', body: JSON.stringify({ userId }) },
      );
    },
    onSuccess: invalidate,
  });
}

export function useUnlinkUser() {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const invalidate = useInvalidator();

  return useMutation({
    mutationFn: async (employeeId: string) => {
      if (mockupMode) return { mockup: true as const };
      const token = await getToken();
      return apiFetch<unknown>(
        `/api/v1/orgs/${currentOrg.id}/employees/${employeeId}/link-user`,
        token,
        { method: 'DELETE' },
      );
    },
    onSuccess: invalidate,
  });
}
